// src/components/ConfirmWrite.tsx
//
// ★★ WAVE 26 — THE ONE CONFIRMATION EVERY IRREVERSIBLE WRITE GOES THROUGH.
//
// ══ THE DEFECT THIS CLOSES ══
//
// A code census of the 40 mutating call sites found that the scripted journeys
// confirmed (approve, reject, settlement release, parameter supersede, the
// re-proration sweep) and twenty-five writes of equal or greater consequence did
// not: every effective-dated supersede on the eleven administration catalogues,
// upload-and-process, the correction composer, archive rotation and its alert
// sweep, the Recovery-Agency alert file, the notification rule and the eCS
// trigger, the support-ticket writes. `ReferenceDataPage` called its own card
// "the one surface that must ask" and `ReprorationConsolePage` said its modal
// gave officers "one pattern to meet" — while a ward-rate supersede that prices
// every ward-scheme bill from a date acted on a single click.
//
// ══ THE SHAPE, and why it is a CARD and not a modal ══
//
// This is `ReferenceDataPage`'s inline `role="alertdialog"` card, extracted
// unchanged in behaviour: the form freezes a SENTENCE describing the change at
// the moment the officer presses Save (never re-derived from a form they may
// since have edited), the card shows that sentence with a confirm and a cancel,
// and the write only happens from the confirm. A card rather than a modal
// because (1) it is what the existing supersede confirmation already is, so the
// eleven catalogue forms end up matching the one that already asked; (2) jsdom
// can exercise it, so every page's DOM test proves the write does NOT happen on
// Save alone; (3) it stays in reading order below the form, so the sentence and
// the fields it describes are on screen together.
//
// ⚠ Friction is proportional to consequence (`ReprorationConsolePage`'s rule):
// a HARMLESS action must not use this — a dry run, a lookup, a filter. A prompt
// on a harmless action teaches people to dismiss the prompt on a harmful one.
//
// ⚠ Focus moves to the CARD when it appears — not to the confirm button — for the
// reason `BillDetailPage`'s repair confirmation records: this step exists to
// interrupt, and landing on the commit control would let a second Enter, the one
// already travelling towards the trigger, commit the write. Landing on the
// `alertdialog` announces the sentence; Tab reaches Confirm, then Cancel. Escape
// cancels. (Measured, too: `SgdsButton.focus()` delegates into a shadow root
// jsdom never renders, so focusing the button cannot even be tested.)
import { useEffect, useId, useRef } from 'react'
import { SgdsButton } from '@govtechsg/sgds-web-component/react'
import { plainProse } from '../lib/plain-words'

export interface ConfirmWriteProps {
  /** The change, as one sentence in the officer's terms, FROZEN when Save was pressed. */
  readonly summary: string
  /** The confirm button's words — say what happens ("Yes, apply it", "Yes, process it"). */
  readonly confirmLabel: string
  readonly onConfirm: () => void
  readonly onCancel: () => void
  /** While the write is in flight: the confirm shows a spinner and both buttons refuse a second press. */
  readonly busy?: boolean
  /** `danger` for money-moving or lock-out writes; the default primary otherwise. */
  readonly variant?: 'primary' | 'danger'
}

export function ConfirmWrite({ summary, confirmLabel, onConfirm, onCancel, busy = false, variant = 'primary' }: ConfirmWriteProps) {
  const summaryId = useId()
  const cardRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    // The card appears in response to Save; focus follows the question, not the
    // form that asked it — `preventScroll` so a long form does not jump.
    //
    // ★ F-04 (UAT re-run): a REAL modal gives focus BACK. The control that opened the question is remembered and
    // focused again when the card goes away (Escape, Cancel, or the write finishing), so a keyboard user is not
    // dropped at the top of the page. Skipped if that control has left the page or is disabled.
    const opener = document.activeElement instanceof HTMLElement ? document.activeElement : null
    cardRef.current?.focus({ preventScroll: true })
    return () => {
      if (opener !== null && opener !== document.body && opener.isConnected) opener.focus({ preventScroll: true })
    }
  }, [])

  /** Tab stays inside the card: from the last control to the first, and Shift+Tab the other way. */
  const trapTab = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key !== 'Tab') return
    const card = cardRef.current
    if (card === null) return
    const stops = [...card.querySelectorAll<HTMLElement>('sgds-button, button, a[href], input, select, textarea')].filter(
      (el) => !el.hasAttribute('disabled'),
    )
    if (stops.length === 0) {
      event.preventDefault()
      return
    }
    const first = stops[0] as HTMLElement
    const last = stops[stops.length - 1] as HTMLElement
    const active = document.activeElement
    if (event.shiftKey && (active === first || active === card)) {
      event.preventDefault()
      last.focus()
    } else if (!event.shiftKey && active === last) {
      event.preventDefault()
      first.focus()
    }
  }

  return (
    <div
      ref={cardRef}
      tabIndex={-1}
      className="pt-card border border-[var(--sgds-border-color-muted)] sm:col-span-2"
      role="alertdialog"
      aria-modal="true"
      aria-label="Confirm this change"
      aria-describedby={summaryId}
      onKeyDown={(event) => {
        if (event.key === 'Escape' && !busy) {
          event.preventDefault()
          event.stopPropagation()
          onCancel()
          return
        }
        trapTab(event)
      }}
    >
      <p id={summaryId} className="m-0 mb-3">
        {plainProse(summary, true)}
      </p>
      <div className="flex gap-2">
        <SgdsButton type="button" variant={variant} loading={busy} disabled={busy} onClick={onConfirm}>
          {confirmLabel}
        </SgdsButton>
        <SgdsButton type="button" variant="outline" disabled={busy} onClick={onCancel}>
          Cancel
        </SgdsButton>
      </div>
    </div>
  )
}
